import React, { useEffect, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import { TTicketData } from 'views/types/tickets';
import dayjs from 'dayjs';
import Card from 'components/card';
import { useTicketsStore } from 'views/store/useTicketsStore';
import { TicketDetailsDialog } from '../TicketDetailsDialog';
import useSWR from 'swr';
import { ticketsService } from 'views/services/ticketsService';
import Pagination from 'components/Pagination/Pagination';
import { useAdminsStore } from 'views/store/useAdminsStore';
import { adminService } from 'views/services/adminService';
import { usePermissions } from 'hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'constants/permissions';

const columnHelper = createColumnHelper<TTicketData & { id: string }>();

const columns = [
  columnHelper.accessor('id', {
    header: 'Ticket ID',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('user.coins_fly_id', {
    header: 'Client ID',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('message', {
    header: 'Message',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('status', {
    header: 'Status',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('category', {
    header: 'Category',
    cell: info => info.getValue(),
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created At',
    cell: info =>
      info.getValue()
        ? dayjs(info.getValue()).format('YYYY-MM-DD HH:mm')
        : '---',
  }),
];

export const TicketsTable = () => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [open, setOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<TTicketData | null>(
    null
  );
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { admins } = useAdminsStore();
  const { canRead } = usePermissions();

  const { tickets, getBy } = useTicketsStore();

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setPage(0);
  };

  const shouldFetch = getBy === 'all';

  const { data } = useSWR(
    shouldFetch
      ? `/api/support-tickets?page=${page + 1}&limit=${itemsPerPage}&getBy=${getBy}`
      : null,
    () =>
      ticketsService.getTickets({
        page: page + 1,
        limit: itemsPerPage,
      }),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    }
  );

  const table = useReactTable({
    data: tickets,
    columns,
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
  });

  useEffect(() => {
    if (!admins?.length && canRead(PERMISSION_RESOURCES.ADMINS)) {
      adminService.getAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTicket && !canRead(PERMISSION_RESOURCES.TICKET_INNER_MENU)) {
      setSelectedTicket(null);
    }
  }, [canRead, selectedTicket]);

  return (
    <div className="mt-8">
      <TicketDetailsDialog
        open={open}
        setOpen={setOpen}
        ticket={selectedTicket}
      />

      <Card>
        <div className="p-4">
          <table className="min-w-full border-collapse">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      className="border-b px-4 py-2 text-left font-medium"
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className={`${
                    canRead(PERMISSION_RESOURCES.TICKET_INNER_MENU)
                      ? 'hover:bg-gray-50 dark:hover:bg-[black]/20 cursor-pointer'
                      : 'opacity-60 cursor-not-allowed'
                  }`}
                  onClick={() => {
                    if (canRead(PERMISSION_RESOURCES.TICKET_INNER_MENU)) {
                      setSelectedTicket(row.original);
                      setOpen(true);
                    }
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id} className="border-b px-4 py-2">
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {data?.meta?.totalPages > 1 && getBy === 'all' && (
            <Pagination
              currentPage={page}
              totalPages={data?.meta?.totalPages}
              totalItems={data?.meta?.totalItems}
              onPageChange={setPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              totalName="Total Transactions"
            />
          )}
        </div>
      </Card>
    </div>
  );
};
